import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Play, Pencil } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  BadgeSoft,
  VbButton,
  VbLoading,
  VbEmpty,
  VbError,
  Callout,
} from "@/components/vb";
import {
  useBuyer,
  useAnalyses,
  useAnalysis,
  useQuestions,
  useTriggerAnalysis,
  vbKeys,
} from "@/hooks/vb/useBuyers";
import { extractVbError } from "./buyer-create/errors";
import { PersonaCard } from "./detail/PersonaCard";
import { ExposureCard } from "./detail/ExposureCard";
import { Big5CoverageCard } from "./detail/Big5CoverageCard";
import { ExposureCardSkeleton, Big5CoverageCardSkeleton } from "./detail/DetailCardSkeletons";
import { AnalysesTable } from "./detail/AnalysesTable";
import { EditBuyerDrawer } from "./detail/EditBuyerDrawer";
import { computeBig5Coverage, questionsChangedSinceRun } from "./detail/big5Coverage";
import { deriveMetricsFromResult } from "./detail/analysisMetrics";

function relativeTime(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDistanceToNow(d, { addSuffix: true });
}

/** VB-C · single-buyer detail. Persona + competitors, the latest completed run's
 *  AI Trust Signal exposure (donut) + Big-5 coverage + headline metrics, and the
 *  analysis history (VB-D). "Edit" opens the drawer (VB-G); "Run analysis" → VB-E. */
export function BuyerDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const buyerId = Number(params.buyerId);
  const validId = Number.isInteger(buyerId) && buyerId > 0;

  const buyer = useBuyer(validId ? buyerId : undefined);
  const analyses = useAnalyses(validId ? buyerId : undefined);
  const questions = useQuestions(validId ? buyerId : undefined);

  // Newest-first by created date, then first DONE. The API already orders
  // -created_ts, but sort explicitly so the donut/Big-5 (which read this run)
  // and the buyer's denormalized metrics describe the SAME latest run even if
  // the list order ever changes.
  const latestDoneId = useMemo(() => {
    const rows = analyses.data;
    if (!rows) return undefined;
    const newestFirst = [...rows].sort((a, b) => b.createdTs.localeCompare(a.createdTs));
    return newestFirst.find((a) => a.status === "DONE")?.id;
  }, [analyses.data]);
  const latestDone = useAnalysis(validId ? buyerId : undefined, latestDoneId);

  // A freshly-triggered run starts PENDING; poll it so the page reflects
  // Queued → Running → Done (the GET reconciles the shadow with Jarts) instead
  // of looking frozen. `runningId` comes from the buyer's latest-of-any-status.
  const latestAny = buyer.data?.latest_analysis;
  const runningId =
    latestAny && (latestAny.status === "PENDING" || latestAny.status === "IN_PROGRESS")
      ? latestAny.id
      : undefined;
  const runningAnalysis = useAnalysis(validId ? buyerId : undefined, runningId, { poll: true });

  const coverage = useMemo(
    () => computeBig5Coverage(latestDone.data?.result, questions.data ?? []),
    [latestDone.data, questions.data],
  );

  // Headline metrics derived from the latest run's own result - see
  // analysisMetrics.ts. The denormalized columns on the buyer can be null for a
  // just-webhook-finalized run, so the result is the authoritative source for
  // the detail view (and keeps the donut consistent with the Big-5 card).
  const derived = useMemo(
    () => deriveMetricsFromResult(latestDone.data?.result),
    [latestDone.data],
  );
  const questionsChanged = useMemo(
    () => questionsChangedSinceRun(latestDone.data?.result, questions.data ?? []),
    [latestDone.data, questions.data],
  );

  // Signals exposed vs the previous DONE run (proto's "↑2 vs last run"). Uses the
  // derived count for the latest run and the prior run's denormalized column;
  // null when there's no comparable prior run (or it isn't cached yet).
  const signalsDelta = useMemo(() => {
    const doneRows = (analyses.data ?? [])
      .filter((a) => a.status === "DONE")
      .sort((a, b) => b.createdTs.localeCompare(a.createdTs));
    const latest = derived.signalsExposed ?? doneRows[0]?.signalsExposedCount ?? null;
    const prior = doneRows[1]?.signalsExposedCount ?? null;
    return latest != null && prior != null ? latest - prior : null;
  }, [analyses.data, derived.signalsExposed]);

  // Reading the analysis detail (above) caches the denormalized metric columns
  // server-side as a side effect. Refetch the buyer + analyses once so the
  // history table row + trend delta pick up those freshly-cached values (the
  // cards already render from `derived`). Ref-guarded to fire at most once per
  // buyer so it can't loop if the columns stay null (e.g. a judgment-less run).
  const qc = useQueryClient();
  const healedRef = useRef<number | null>(null);
  useEffect(() => {
    const m = buyer.data?.metrics;
    const needsHeal = m == null || m.signals_exposed == null;
    if (
      latestDone.data?.status === "DONE" &&
      latestDone.data.result &&
      needsHeal &&
      healedRef.current !== buyerId
    ) {
      healedRef.current = buyerId;
      qc.invalidateQueries({ queryKey: vbKeys.buyer(buyerId) });
      qc.invalidateQueries({ queryKey: vbKeys.analyses(buyerId) });
    }
  }, [latestDone.data, buyer.data?.metrics, buyerId, qc]);

  // As the polled in-flight run advances, refresh the history list (so its
  // status moves Queued → Running) and, when it finishes, the buyer (so the
  // banner clears and the new run's metrics/cards appear).
  const runningStatus = runningAnalysis.data?.status;
  useEffect(() => {
    if (!runningId || !runningStatus) return;
    qc.invalidateQueries({ queryKey: vbKeys.analyses(buyerId) });
    if (runningStatus === "DONE" || runningStatus === "FAILED") {
      qc.invalidateQueries({ queryKey: vbKeys.buyer(buyerId) });
    }
  }, [runningStatus, runningId, buyerId, qc]);

  const triggerM = useTriggerAnalysis();
  const [runError, setRunError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const runAnalysis = async () => {
    if (triggerM.isPending) return;
    setRunError(null);
    try {
      const shadow = await triggerM.mutateAsync({
        buyerId,
        questionsCount: questions.data?.length,
      });
      navigate(`/virtual-buyer/${buyerId}/run/${shadow.id}`);
    } catch (err) {
      const e = extractVbError(err);
      if (e.status === 409) {
        const existingId = (err as { response?: { data?: { existingAnalysisId?: number } } })
          .response?.data?.existingAnalysisId;
        if (existingId != null) {
          navigate(`/virtual-buyer/${buyerId}/run/${existingId}`);
          return;
        }
      }
      setRunError(e.detail);
    }
  };

  if (!validId || buyer.isError) {
    return (
      <VbError
        title="Buyer not found"
        description="We couldn't load this buyer. It may have been deleted, or the link is wrong."
        onRetry={validId ? () => buyer.refetch() : undefined}
      />
    );
  }
  if (buyer.isPending) {
    return <VbLoading rows={6} />;
  }

  const b = buyer.data;
  const metrics = b.metrics;
  const latest = b.latest_analysis;
  const running = latest != null && (latest.status === "PENDING" || latest.status === "IN_PROGRESS");
  // Freshest status of the in-flight run (the poll updates faster than the
  // buyer's prefetched latest_analysis), so the banner can say queued vs running.
  const liveRunStatus = runningAnalysis.data?.status ?? latest?.status;
  const failedLatest = latest?.status === "FAILED";
  const hasDone = latestDoneId != null;
  // Prefer the live run's derived numbers; fall back to the denormalized
  // columns (e.g. before the result loads, or on a result-fetch error).
  const signalsExposed = derived.signalsExposed ?? metrics?.signals_exposed ?? null;
  const visibility = derived.visibility ?? metrics?.visibility ?? null;
  const sov = derived.sov ?? metrics?.sov ?? null;
  const edited = b.edited_fields.length > 0;
  const created = relativeTime(b.created_ts);
  const compCount = b.competitors.length;
  const runCount = analyses.data?.length ?? 0;

  return (
    <div className="vb-detail">
      <header className="vb-page-hd">
        <div className="vb-page-hd__left">
          <Link className="vb-detail-crumb" to="/virtual-buyer">
            <ChevronLeft size={13} aria-hidden="true" />
            Virtual Buyers
          </Link>
          <h1 className="vb-page-hd__title">
            {b.name}
            <BadgeSoft tone={edited ? "edited" : "suggested"}>
              {edited ? "Edited" : "Suggested"}
            </BadgeSoft>
          </h1>
          <p className="vb-page-hd__sub">
            {compCount} competitor{compCount === 1 ? "" : "s"} tracked
            {created ? ` · created ${created}` : ""}
          </p>
        </div>
        <div className="vb-page-hd__actions">
          <VbButton variant="secondary" onClick={() => setEditOpen(true)}>
            <Pencil size={14} aria-hidden="true" />
            Edit
          </VbButton>
          <VbButton onClick={() => void runAnalysis()} disabled={triggerM.isPending || running}>
            <Play size={14} aria-hidden="true" />
            {triggerM.isPending ? "Starting…" : "Run analysis"}
          </VbButton>
        </div>
      </header>

      {runError && (
        <div className="limit-flash show" role="alert" style={{ marginBottom: 12 }}>
          {runError}
        </div>
      )}

      <div className="vb-detail-grid">
        <PersonaCard buyer={b} onEdit={() => setEditOpen(true)} />

        <div className="vb-detail-right">
          {running && latest && (
            <Callout tone="info" role="status">
              {liveRunStatus === "PENDING"
                ? "An analysis is queued and will start shortly."
                : "An analysis is running."}{" "}
              <Link to={`/virtual-buyer/${buyerId}/run/${latest.id}`}>View progress</Link>
            </Callout>
          )}
          {failedLatest && (
            <Callout tone="danger" role="status">
              The most recent analysis failed.{" "}
              <button type="button" className="vb-link" onClick={() => void runAnalysis()}>
                Run again
              </button>
            </Callout>
          )}

          {analyses.isPending ? (
            <div className="vb-cards-row">
              <ExposureCardSkeleton />
              <Big5CoverageCardSkeleton />
            </div>
          ) : hasDone ? (
            <div className="vb-cards-row">
              {latestDone.isPending ? (
                <>
                  <ExposureCardSkeleton />
                  <Big5CoverageCardSkeleton />
                </>
              ) : latestDone.isError ? (
                <>
                  <ExposureCard signalsExposed={signalsExposed} signalsDelta={signalsDelta} />
                  <div className="vb-dcard">
                    <div className="vb-dcard__hd">
                      <span className="vb-dcard__lbl">Big 5 coverage</span>
                    </div>
                    <div className="vb-dcard-empty">
                      <strong>Couldn't load the latest run</strong>
                      <button type="button" className="vb-link" onClick={() => latestDone.refetch()}>
                        Retry
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <ExposureCard signalsExposed={signalsExposed} signalsDelta={signalsDelta} />
                  {questions.isPending ? (
                    <Big5CoverageCardSkeleton />
                  ) : (
                    <Big5CoverageCard
                      coverage={coverage}
                      visibility={visibility}
                      sov={sov}
                      visibilityDelta={metrics?.visibility_delta ?? null}
                      questionCount={questions.data?.length ?? 0}
                      questionsChanged={questionsChanged}
                    />
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="vb-dcard">
              <div className="vb-dcard__hd">
                <span className="vb-dcard__lbl">AI Trust Signal exposure</span>
              </div>
              <div className="vb-dcard-empty">
                <strong>No completed analysis yet</strong>
                {running
                  ? "Your first run is in progress. Exposure and Big-5 coverage appear here once it finishes."
                  : "Run an analysis to see how AI surfaces your AI Trust Signals and Big-5 coverage."}
              </div>
            </div>
          )}

          <div className="vb-panel">
            <div className="vb-panel-hd">
              <h2>Analyses</h2>
              <div className="vb-panel-hd__right">
                <span className="vb-runs-note">
                  {runCount} run{runCount === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            {analyses.isPending ? (
              <div style={{ padding: 16 }}>
                <VbLoading rows={3} />
              </div>
            ) : analyses.isError ? (
              <VbError description="We couldn't load this buyer's runs." onRetry={() => analyses.refetch()} />
            ) : runCount === 0 ? (
              <VbEmpty
                title="No runs yet"
                description="Run an analysis to see how this buyer sees you. Each run is saved here."
              />
            ) : (
              <AnalysesTable
                analyses={analyses.data}
                buyerId={buyerId}
                latestDoneId={latestDoneId}
                latestSignals={signalsExposed}
              />
            )}
          </div>
        </div>
      </div>

      <EditBuyerDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        buyer={b}
        questions={questions.data ?? []}
      />
    </div>
  );
}

export default BuyerDetailPage;
